import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import cn from 'classnames';
import { $getRoot, EditorState } from 'lexical';
import { FC, KeyboardEvent, useCallback, useMemo, useState } from 'react';

import { EditablePlugin } from '../SlotEditor/plugins/EditablePlugin';
import { SingleLinePlugin } from '../SlotEditor/plugins/SingleLinePlugin';

import styles from './PlainLexicalEditor.module.scss';
import { PlainInitialValuePlugin } from './plugins/PlainInitialValuePlugin';

type PlainLexicalEditorProps = {
  className?: string;
  value: string;
  placeholder?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  onChange: (text: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onEnter?: () => void;
  onTab?: (isShiftPressed: boolean) => void;
};

export const PlainLexicalEditor: FC<PlainLexicalEditorProps> = (props) => {
  const {
    className,
    value,
    placeholder,
    readOnly,
    autoFocus,
    onChange,
    onFocus,
    onBlur,
    onEnter,
    onTab,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const initialConfig = useMemo(
    () => ({
      namespace: 'PlainLexicalEditor',
      theme: {
        root: styles.editorRoot,
        paragraph: styles.editorParagraph,
      },
      nodes: [],
      editable: !readOnly,
      onError: (error: Error) => {
        console.error('PlainLexicalEditor error:', error);
      },
    }),
    [readOnly],
  );

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent().replace(/\n/g, ' ');
        onChange(text);
      });
    },
    [onChange],
  );

  const showGhostBorder = isHovered && !isFocused && !readOnly;
  const showFullBorder = isFocused && !readOnly;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (readOnly) {
      return;
    }

    if (event.key === 'Enter' && onEnter) {
      event.preventDefault();
      onEnter();
      return;
    }

    if (event.key === 'Tab' && onTab) {
      event.preventDefault();
      onTab(event.shiftKey);
    }
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        className={cn(
          styles.container,
          showFullBorder && styles.focused,
          showGhostBorder && styles.hovered,
          readOnly && styles.readOnly,
          className,
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => {
          setIsFocused(true);
          onFocus?.();
        }}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
      >
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              className={cn(
                styles.contentEditable,
                readOnly && styles.contentEditableReadOnly,
              )}
              onKeyDown={handleKeyDown}
            />
          }
          placeholder={
            placeholder ? (
              <span className={styles.placeholder}>{placeholder}</span>
            ) : null
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} />
        <PlainInitialValuePlugin value={value} />
        <SingleLinePlugin />
        <EditablePlugin readOnly={readOnly ?? false} />
        {autoFocus && <AutoFocusPlugin />}
      </div>
    </LexicalComposer>
  );
};
